/**
 * Typed fetch wrapper. All HTTP calls to the backend go through `api()`.
 *
 * - Automatically attaches the JWT from `auth` store when present.
 * - Throws `ApiError` on non-2xx so react-query can surface errors.
 * - Reads VITE_API_URL for prod builds; dev uses Vite proxy at /api.
 */
import { clearAuth, getToken } from "./auth";
import { queryClient } from "./queryClient";
import { ensureFreshToken } from "./session";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipAuth?: boolean;
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {}
): Promise<T> {
  const { body, skipAuth, headers: extraHeaders, ...rest } = opts;

  const headers = new Headers(extraHeaders);
  if (!headers.has("Content-Type") && body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (!skipAuth) {
    // Refresh proactively when the JWT is about to expire. The auth
    // endpoints themselves are exempt so /api/auth/refresh can't recurse.
    const token = path.startsWith("/api/auth/")
      ? getToken()
      : await ensureFreshToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const url = API_BASE
    ? `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`
    : path;

  const res = await fetch(url, {
    ...rest,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Token expired/invalid: cancel any in-flight queries and clear the
      // cache BEFORE wiping the token — otherwise react-query keeps
      // returning stale data for a tick and the UI flashes the previous
      // user's state before ProtectedRoute redirects to /login.
      queryClient.cancelQueries();
      queryClient.clear();
      clearAuth();
    }
    const detail =
      (parsed as { detail?: string; message?: string } | null)?.detail ??
      (parsed as { message?: string } | null)?.message ??
      res.statusText ??
      "request failed";
    throw new ApiError(res.status, detail, parsed);
  }

  return parsed as T;
}
